import { useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import PlacowkaService from '@/services/PlacowkaService'
import MiastoService from '@/services/MiastoService'

export default function usePlacowkiModal(loadData) {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [editingItem, setEditingItem] = useState(null)
	const [formName, setFormName] = useState('')
	const [formAdres, setFormAdres] = useState('')
	const [formMiastoId, setFormMiastoId] = useState(null)
	const [formMiastoName, setFormMiastoName] = useState('')
	const [formGminaId, setFormGminaId] = useState(null)
	const [formGminaName, setFormGminaName] = useState('')
	const [formPowiatId, setFormPowiatId] = useState(null)
	const [formPowiatName, setFormPowiatName] = useState('')
	const [formWojewodztwoId, setFormWojewodztwoId] = useState(null)
	const [formWojewodztwoName, setFormWojewodztwoName] = useState('')
	const [formTypId, setFormTypId] = useState(null)
	const [formTypName, setFormTypName] = useState('')
	const [formNip, setFormNip] = useState('')
	const [isSaving, setIsSaving] = useState(false)
	const [isMiastoSelectOpen, setIsMiastoSelectOpen] = useState(false)
	const [isGminaSelectOpen, setIsGminaSelectOpen] = useState(false)
	const [isPowiatSelectOpen, setIsPowiatSelectOpen] = useState(false)
	const [isWojewodztwoSelectOpen, setIsWojewodztwoSelectOpen] = useState(false)
	const [isTypSelectOpen, setIsTypSelectOpen] = useState(false)

	const resetForm = () => {
		setFormName('')
		setFormAdres('')
		setFormMiastoId(null)
		setFormMiastoName('')
		setFormGminaId(null)
		setFormGminaName('')
		setFormPowiatId(null)
		setFormPowiatName('')
		setFormWojewodztwoId(null)
		setFormWojewodztwoName('')
		setFormTypId(null)
		setFormTypName('')
		setFormNip('')
	}

	const getRef = (value, field = 'nazwa') => {
		const id = typeof value === 'object' ? value?._id || value?.id || null : value || null
		const name = typeof value === 'object' ? value?.[field] || '' : ''
		return { id, name }
	}

	const openCreateModal = () => {
		setEditingItem(null)
		resetForm()
		setIsModalOpen(true)
	}

	const openEditModal = item => {
		setEditingItem(item)
		setFormName(item?.nazwa || '')
		setFormAdres(item?.adres || '')

		const miastoRef = getRef(item?.id_miasto, 'nazwa')
		setFormMiastoId(miastoRef.id)
		setFormMiastoName(miastoRef.name)

		const gminaRef = getRef(item?.id_gmina, 'nazwa')
		setFormGminaId(gminaRef.id)
		setFormGminaName(gminaRef.name)

		const powiatRef = getRef(item?.id_powiat, 'nazwa')
		setFormPowiatId(powiatRef.id)
		setFormPowiatName(powiatRef.name)

		const wojRef = getRef(item?.id_wojewodztwo, 'nazwa')
		setFormWojewodztwoId(wojRef.id)
		setFormWojewodztwoName(wojRef.name)

		const typRef = getRef(item?.id_typ, 'typ')
		setFormTypId(typRef.id)
		setFormTypName(typRef.name)

		setFormNip(item?.nip || '')

		setIsModalOpen(true)
	}

	const closeModal = () => {
		setIsModalOpen(false)
		setEditingItem(null)
		resetForm()
		setIsMiastoSelectOpen(false)
		setIsGminaSelectOpen(false)
		setIsPowiatSelectOpen(false)
		setIsWojewodztwoSelectOpen(false)
		setIsTypSelectOpen(false)
		setIsSaving(false)
	}

	const handleSubmit = async e => {
		e.preventDefault()
		const payload = {
			nazwa: formName.trim(),
			adres: formAdres.trim(),
			id_miasto: formMiastoId,
			id_gmina: formGminaId,
			id_powiat: formPowiatId,
			id_wojewodztwo: formWojewodztwoId,
			id_typ: formTypId,
			nip: formNip.trim(),
		}
		if (
			!payload.nazwa ||
			!payload.adres ||
			!payload.id_miasto ||
			!payload.id_gmina ||
			!payload.id_powiat ||
			!payload.id_wojewodztwo ||
			!payload.id_typ
		)
			return

		setIsSaving(true)
		const result = editingItem?._id
			? await PlacowkaService.update(editingItem._id, payload)
			: await PlacowkaService.create(payload)

		if (!result?.success) {
			Swal.fire({ title: 'Error', text: result?.error || 'Nie udalo sie zapisac placowki', icon: 'error' })
			setIsSaving(false)
			return
		}

		closeModal()
		await loadData()
	}

	const handleDelete = async item => {
		const confirm = await Swal.fire({
			title: 'Czy na pewno?',
			text: `Usunac placowke "${item?.nazwa || '-'}"?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Tak, usun',
			cancelButtonText: 'Anuluj',
		})
		if (!confirm.isConfirmed) return
		const result = await PlacowkaService.delete(item?._id)
		if (!result?.success) {
			Swal.fire({ title: 'Error', text: result?.error || 'Nie udalo sie usunac placowki', icon: 'error' })
			return
		}
		await loadData()
	}

	const buttons = useMemo(() => [{ type: 'add', label: 'Dodaj nowe', onClick: openCreateModal }], [])

	return {
		isModalOpen,
		editingItem,
		formName,
		setFormName,
		formAdres,
		setFormAdres,
		formMiastoId,
		formMiastoName,
		formGminaId,
		formGminaName,
		formPowiatId,
		formPowiatName,
		formWojewodztwoId,
		formWojewodztwoName,
		formTypId,
		formTypName,
		formNip,
		setFormNip,
		isSaving,
		isMiastoSelectOpen,
		isGminaSelectOpen,
		isPowiatSelectOpen,
		isWojewodztwoSelectOpen,
		isTypSelectOpen,
		openEditModal,
		handleDelete,
		closeModal,
		handleSubmit,
		openMiastoSelectModal: () => setIsMiastoSelectOpen(true),
		closeMiastoSelectModal: () => setIsMiastoSelectOpen(false),
		applyMiastoSelection: async (id, name) => {
			const nextMiastoId = id || null
			setFormMiastoId(nextMiastoId)
			setFormMiastoName(name || '')

			if (nextMiastoId) {
				const miastoDetails = await MiastoService.getById(nextMiastoId)
				const miastoData = miastoDetails?.data || null

				const gminaRef = getRef(miastoData?.id_gmina, 'nazwa')
				const powiatRef = getRef(
					miastoData?.id_powiat || miastoData?.id_gmina?.id_powiat,
					'nazwa',
				)
				const wojewodztwoRef = getRef(
					miastoData?.id_wojewodztwo ||
						miastoData?.id_powiat?.id_wojewodztwo ||
						miastoData?.id_gmina?.id_powiat?.id_wojewodztwo,
					'nazwa',
				)

				if (gminaRef.id) {
					setFormGminaId(gminaRef.id)
					setFormGminaName(gminaRef.name)
				}
				if (powiatRef.id) {
					setFormPowiatId(powiatRef.id)
					setFormPowiatName(powiatRef.name)
				}
				if (wojewodztwoRef.id) {
					setFormWojewodztwoId(wojewodztwoRef.id)
					setFormWojewodztwoName(wojewodztwoRef.name)
				}
			}

			setIsMiastoSelectOpen(false)
		},
		openGminaSelectModal: () => setIsGminaSelectOpen(true),
		closeGminaSelectModal: () => setIsGminaSelectOpen(false),
		applyGminaSelection: (id, name) => {
			const nextGminaId = id || null
			setFormGminaId(nextGminaId)
			setFormGminaName(name || '')
			if (nextGminaId !== formGminaId) {
				setFormMiastoId(null)
				setFormMiastoName('')
			}
			setIsGminaSelectOpen(false)
		},
		openPowiatSelectModal: () => setIsPowiatSelectOpen(true),
		closePowiatSelectModal: () => setIsPowiatSelectOpen(false),
		applyPowiatSelection: (id, name) => {
			const nextPowiatId = id || null
			setFormPowiatId(nextPowiatId)
			setFormPowiatName(name || '')
			if (nextPowiatId !== formPowiatId) {
				setFormGminaId(null)
				setFormGminaName('')
				setFormMiastoId(null)
				setFormMiastoName('')
			}
			setIsPowiatSelectOpen(false)
		},
		openWojewodztwoSelectModal: () => setIsWojewodztwoSelectOpen(true),
		closeWojewodztwoSelectModal: () => setIsWojewodztwoSelectOpen(false),
		applyWojewodztwoSelection: (id, name) => {
			const nextWojewodztwoId = id || null
			setFormWojewodztwoId(nextWojewodztwoId)
			setFormWojewodztwoName(name || '')
			if (nextWojewodztwoId !== formWojewodztwoId) {
				setFormPowiatId(null)
				setFormPowiatName('')
				setFormGminaId(null)
				setFormGminaName('')
				setFormMiastoId(null)
				setFormMiastoName('')
			}
			setIsWojewodztwoSelectOpen(false)
		},
		openTypSelectModal: () => setIsTypSelectOpen(true),
		closeTypSelectModal: () => setIsTypSelectOpen(false),
		applyTypSelection: (id, name) => {
			setFormTypId(id || null)
			setFormTypName(name || '')
			setIsTypSelectOpen(false)
		},
		buttons,
	}
}
